import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ACCOUNT_CREATION_SUCCESSFUL } from "../Constants/CommonConstants";
import {
  API_INITIAL_STATE,
  BUILDER_TYPE_FULFILLED,
  BUILDER_TYPE_PENDING,
  BUILDER_TYPE_REJECTED,
  SLICE_ACCOUNT_CREATION,
  THUNK_POST_ACCOUNT,
  THUNK_POST_CREATE_ACCOUNT,
  THUNK_POST_OTP,
  THUNK_POST_PASSWORD,
  THUNK_POST_USERNAME,
  THUNK_POST_VALIDATE_OTP,
  THUNK_POST_VERIFICATION_METHOD,
} from "../Constants/SliceAndThunkConstants";
import { OTP_SENT_SUCCESSFULLY } from "../Constants/VerifyAccountConstants";

import {
  postAccountService,
  postCreateAccountService,
  postPasswordService,
  postUsernameService,
  postVerificationMethodService,
} from "../Services/AccountCreationService";
import {
  apiSliceBuilderService,
  fetchConsumerUserRoleData,
} from "../Services/CommonServices";

/**
 * Possible Statuses for API calls are API_STATUS_IDLE, API_STATUS_SUCCESS, API_STATUS_LOADING, API_STATUS_FAILURE
 */
const initialState = {
  firstName: "",
  lastName: "",
  dateOfBirth: "",
  preferredContact: "",
  email: "",
  emailLanguage: "",
  phone: "",
  userName: "",
  password: "",
  usernameApiResponse: "",
  passwordApiResponse: "",
  userType: "",
  locale: "",
  prefVerificationMethod: "",
  otpType: "",
  otp: "",
  loginToken: "",
  mockSmsOTP: "",
  hapNumber: "",
  ssn: "",
  streetAddress: "",
  appNumber: 0,
  zipCode: 0,
  accountFound: "",
  authToken: "",
  sendOTPDescription: "",
  validateOTPDescription: "",
  postVerificationMethodResponse: API_INITIAL_STATE,
  postUsernameResponse: API_INITIAL_STATE,
  postPasswordResponse: API_INITIAL_STATE,
  postAccountResponse: API_INITIAL_STATE,
  postCreateAccountResponse: API_INITIAL_STATE,
  postOTPResponse: API_INITIAL_STATE,
  postValidateOTPResponse: API_INITIAL_STATE,
};

export const postVerificationMethod = createAsyncThunk(
  `${SLICE_ACCOUNT_CREATION}/${THUNK_POST_VERIFICATION_METHOD}`,
  async ({ data, apiAction }, { rejectWithValue }) => {
    try {
      return await postVerificationMethodService(data, apiAction);
    } catch (error) {
      return rejectWithValue(error?.response);
    }
  }
);

export const postUsername = createAsyncThunk(
  `${SLICE_ACCOUNT_CREATION}/${THUNK_POST_USERNAME}`,
  async ({ data, apiAction }, { rejectWithValue }) => {
    try {
      return await postUsernameService(data, apiAction);
    } catch (error) {
      return rejectWithValue(error?.response);
    }
  }
);

export const postPassword = createAsyncThunk(
  `${SLICE_ACCOUNT_CREATION}/${THUNK_POST_PASSWORD}`,
  async ({ data, apiAction }, { rejectWithValue }) => {
    try {
      return await postPasswordService(data, apiAction);
    } catch (error) {
      return rejectWithValue(error?.response);
    }
  }
);

export const postAccount = createAsyncThunk(
  `${SLICE_ACCOUNT_CREATION}/${THUNK_POST_ACCOUNT}`,
  async (payload) => {
    const response = await postAccountService(payload);
    return response;
  }
);

export const postCreateAccount = createAsyncThunk(
  `${SLICE_ACCOUNT_CREATION}/${THUNK_POST_CREATE_ACCOUNT}`,
  async (payload) => {
    const response = await postCreateAccountService(payload);
    return response;
  }
);

export const postOTP = createAsyncThunk(
  `${SLICE_ACCOUNT_CREATION}/${THUNK_POST_OTP}`,
  async (payload) => {
    const response = await postAccountService(payload);
    return response;
  }
);

export const postValidateOTP = createAsyncThunk(
  `${SLICE_ACCOUNT_CREATION}/${THUNK_POST_VALIDATE_OTP}`,
  async (payload, { dispatch, getState, rejectWithValue }) => {
    try {
      const response = await postAccountService(payload);
      if (
        response?.data?.message?.description === ACCOUNT_CREATION_SUCCESSFUL
      ) {
        const accountData = getState().accountCreation;
        const linkApplicationData = getState().linkApplication;
        const consumerUserRole = fetchConsumerUserRoleData(
          getState().referenceData.referenceOptions.userRole
        );

        const payload = {
          firstName: accountData.firstName,
          lastName: accountData.lastName,
          username: accountData.userName,
          userRole: consumerUserRole?.value,
          dateOfBirth: accountData.dateOfBirth,
        };

        if (linkApplicationData.responseData.hapId) {
          payload.hapId = linkApplicationData.responseData.hapId;
        }

        await dispatch(postCreateAccount(payload));
      }

      return response;
    } catch (error) {
      return rejectWithValue(error?.response);
    }
  }
);

export const accountCreationSlice = createSlice({
  name: "accountCreation",
  initialState,
  reducers: {
    setNameAndContactDetails: (state, action) => {
      Object.assign(state, action.payload);
    },
    saveAccountSetupInformation: (state, action) => {
      Object.assign(state, action.payload);
    },
    resetMockSmsOTP: (state) => {
      state.mockSmsOTP = "";
    },
    resetValidateOTPDescription: (state) => {
      state.validateOTPDescription = "";
    },
    resetClientAccountSendOTPDescription: (state) => {
      state.sendOTPDescription = "";
    },
    clearAccountCreationApiResponse: (state, action) => {
      const responseName = `${action.payload}Response`;
      if (!initialState[responseName]) {
        return;
      }
      state[responseName] = initialState[responseName];
    },
    clearAccountCreationSlice: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(postUsername.pending, (state) => {
      state.postUsernameResponse = apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(postUsername.rejected, (state, action) => {
      if (action?.payload?.status === 406) {
        state.postUsernameResponse = apiSliceBuilderService(
          BUILDER_TYPE_REJECTED,
          action
        );
        return;
      }
      throw new Error(action?.payload?.data.error);
    });
    builder.addCase(postUsername.fulfilled, (state, action) => {
      state.postUsernameResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );
    });
    builder.addCase(postPassword.pending, (state) => {
      state.postPasswordResponse = apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(postPassword.rejected, (state, action) => {
      if (action?.payload?.status === 406) {
        state.postPasswordResponse = apiSliceBuilderService(
          BUILDER_TYPE_REJECTED,
          action
        );
        return;
      }
      throw new Error(action?.payload?.data.error);
    });
    builder.addCase(postPassword.fulfilled, (state, action) => {
      state.postPasswordResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );
    });
    builder.addCase(postAccount.pending, (state) => {
      state.postAccountResponse = apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(postAccount.fulfilled, (state, action) => {
      state.postAccountResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );
      const { authToken } = action.payload.data.data;
      state.sendOTPDescription =
        state?.postAccountResponse?.message?.description;
      state.authToken = authToken;
      if (action?.payload?.data?.data?.mockSmsOtp) {
        state.mockSmsOTP = action?.payload?.data?.data?.mockSmsOtp;
      }
    });

    builder.addCase(postValidateOTP.pending, (state) => {
      state.postValidateOTPResponse =
        apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(postValidateOTP.rejected, (state, action) => {
      if (action?.payload?.status === 406) {
        state.postValidateOTPResponse = apiSliceBuilderService(
          BUILDER_TYPE_REJECTED,
          action
        );
        const validateOTPDescription = state?.postValidateOTPResponse?.error
          ?.message
          ? state?.postValidateOTPResponse?.error?.message
          : state?.postValidateOTPResponse?.message?.description;
        state.validateOTPDescription = validateOTPDescription;

        if (action?.payload?.data?.data?.authToken) {
          state.authToken = action?.payload?.data?.data?.authToken;
        }
        return;
      }
      throw new Error(action?.payload?.data.error);
    });
    builder.addCase(postValidateOTP.fulfilled, (state, action) => {
      state.postValidateOTPResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );
    });

    builder.addCase(postCreateAccount.pending, (state) => {
      state.postCreateAccountResponse =
        apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(postCreateAccount.fulfilled, (state, action) => {
      state.postCreateAccountResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );
    });
    builder.addCase(postOTP.pending, (state) => {
      state.postOTPResponse = apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(postOTP.fulfilled, (state, action) => {
      state.postOTPResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );
      const { data } = action.payload.data;
      if (state.postOTPResponse.message.description === OTP_SENT_SUCCESSFULLY) {
        state.authToken = data?.authToken;
        state.mockSmsOTP = data?.mockSmsOtp;
      }
    });

    builder.addCase(postVerificationMethod.pending, (state) => {
      state.postVerificationMethodResponse =
        apiSliceBuilderService(BUILDER_TYPE_PENDING);
    });
    builder.addCase(postVerificationMethod.rejected, (state, action) => {
      if (action?.payload?.status === 406) {
        state.postVerificationMethodResponse = apiSliceBuilderService(
          BUILDER_TYPE_REJECTED,
          action
        );
        return;
      }
      throw new Error(action?.payload?.data.error);
    });
    builder.addCase(postVerificationMethod.fulfilled, (state, action) => {
      state.postVerificationMethodResponse = apiSliceBuilderService(
        BUILDER_TYPE_FULFILLED,
        action
      );
    });
  },
});

export const {
  setNameAndContactDetails,
  saveAccountSetupInformation,
  resetMockSmsOTP,
  clearAccountCreationSlice,
  resetValidateOTPDescription,
  resetAccountCreationStatus,
  clearAccountCreationApiResponse,
  resetClientAccountSendOTPDescription,
} = accountCreationSlice.actions;

export default accountCreationSlice.reducer;
